import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Button, Box, Alert, CircularProgress, Paper,
  TextField, Select, MenuItem, FormControl, InputLabel, Grid,
  ToggleButtonGroup, ToggleButton, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Divider, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { apiRequest } from '../utils/api';

export default function RobinhoodFeatureTest() {
  const [libraryStatus, setLibraryStatus] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [mfa, setMfa] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState({});
  const [errors, setErrors] = useState({});
  const [symbols, setSymbols] = useState('AAPL,TSLA');

  // Order entry form state
  const [orderAssetType, setOrderAssetType] = useState('EQUITY');
  const [orderSymbol, setOrderSymbol] = useState('');
  const [orderSide, setOrderSide] = useState('buy');
  const [orderType, setOrderType] = useState('MARKET');
  const [orderQuantity, setOrderQuantity] = useState('1');
  const [orderPrice, setOrderPrice] = useState('');
  const [orderTimeInForce, setOrderTimeInForce] = useState('gtc');
  const [orderExpiration, setOrderExpiration] = useState('');
  const [orderStrike, setOrderStrike] = useState('');
  const [orderPutCall, setOrderPutCall] = useState('call');
  const [orderPositionEffect, setOrderPositionEffect] = useState('open');
  const [orderSpreadMode, setOrderSpreadMode] = useState('single'); // 'single' | 'multi'
  const [orderDirection, setOrderDirection] = useState('debit'); // 'debit' | 'credit'
  const [orderLegs, setOrderLegs] = useState([
    { expirationDate: '', strike: '', optionType: 'call', action: 'buy', effect: 'open', ratioQuantity: 1 },
    { expirationDate: '', strike: '', optionType: 'call', action: 'sell', effect: 'open', ratioQuantity: 1 },
  ]);
  const [orderConfirmOpen, setOrderConfirmOpen] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [orderSubmitting, setOrderSubmitting] = useState(false);
  const [orderError, setOrderError] = useState(null);

  useEffect(() => { checkLibrary(); loadAccounts(); }, []);
  useEffect(() => { if (orderAssetType === 'OPTION') setOrderType('LIMIT'); }, [orderAssetType]);

  const setLoad = (key, val) => setLoading(prev => ({ ...prev, [key]: val }));
  const setErr = (key, err) => {
    let msg = null;
    if (err) msg = err.response?.data?.error || err.message || String(err);
    setErrors(prev => ({ ...prev, [key]: msg }));
  };

  const call = async (key, url, method = 'GET', body = null) => {
    setLoad(key, true); setErr(key, null); setResult({ key, loading: true });
    try {
      const opts = {};
      if (body) { opts.method = method; opts.body = JSON.stringify(body); }
      const r = await apiRequest(url, opts);
      setResult({ key, data: r.data });
    } catch (e) {
      const msg = e.response?.data?.error || e.message || String(e);
      setErr(key, e);
      setResult({ key, error: msg });
    } finally {
      setLoad(key, false);
    }
  };

  const checkLibrary = async () => {
    setLoad('library', true); setErr('library', null); setLibraryStatus(null);
    try { const r = await apiRequest('/api/robinhood-api/validate-library'); setLibraryStatus(r.data); }
    catch (e) { setErr('library', e); }
    finally { setLoad('library', false); }
  };

  const loadAccounts = async () => {
    setLoad('accounts', true); setErr('accounts', null);
    try {
      const r = await apiRequest('/api/accounts');
      const all = r.data || [];
      const rh = all.filter(a => ['robinhood', 'robinhood-api'].includes(String(a.apiType).toLowerCase()));
      setAccounts(rh);
      if (rh.length && !accountId) setAccountId(rh[0].id);
    } catch (e) { setErr('accounts', e); }
    finally { setLoad('accounts', false); }
  };

  const testConnection = () => {
    call('connection', `/api/robinhood-api/test-connection/${accountId}`, 'POST', { username, password, mfa });
  };
  const fetchPositions = () => call('positions', `/api/robinhood-api/positions/${accountId}`);
  const fetchOrders = () => call('orders', `/api/robinhood-api/orders/${accountId}`);
  const fetchOrderHistory = () => call('orderHistory', `/api/robinhood-api/order-history/${accountId}`);
  const fetchProfile = () => call('profile', `/api/robinhood-api/profile/${accountId}`);
  const fetchDividends = () => call('dividends', `/api/robinhood-api/dividends/${accountId}`);
  const fetchQuotes = () => {
    const syms = symbols.split(',').map(s => s.trim()).filter(Boolean);
    call('quotes', `/api/robinhood-api/quotes/${accountId}`, 'POST', { symbols: syms });
  };

  const updateLeg = (idx, field, value) => {
    setOrderLegs(prev => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  };
  const addLeg = () => {
    setOrderLegs(prev => [...prev, { expirationDate: '', strike: '', optionType: 'call', action: 'buy', effect: 'open', ratioQuantity: 1 }]);
  };
  const removeLeg = (idx) => {
    setOrderLegs(prev => prev.filter((_, i) => i !== idx));
  };

  const buildOrderPayload = () => {
    const payload = {
      assetType: orderAssetType,
      symbol: orderSymbol.trim().toUpperCase(),
      side: orderSide,
      orderType,
      quantity: parseFloat(orderQuantity) || 0,
      timeInForce: orderTimeInForce,
    };
    if (orderType === 'LIMIT') payload.price = parseFloat(orderPrice) || 0;
    if (orderAssetType === 'OPTION') {
      if (orderSpreadMode === 'multi') {
        payload.legs = orderLegs.map(l => ({
          expirationDate: l.expirationDate,
          strike: parseFloat(l.strike) || 0,
          optionType: l.optionType,
          action: l.action,
          effect: l.effect,
          ratioQuantity: parseInt(l.ratioQuantity) || 1,
        }));
        payload.direction = orderDirection;
      } else {
        payload.expirationDate = orderExpiration;
        payload.strike = parseFloat(orderStrike) || 0;
        payload.optionType = orderPutCall;
        payload.positionEffect = orderPositionEffect;
      }
    }
    return payload;
  };

  const isOrderValid = () => {
    if (!accountId || !orderSymbol.trim() || (parseFloat(orderQuantity) || 0) <= 0) return false;
    if (orderType === 'LIMIT' && (!orderPrice || parseFloat(orderPrice) <= 0)) return false;
    if (orderAssetType === 'OPTION') {
      if (orderType !== 'LIMIT') return false;
      if (orderSpreadMode === 'multi') {
        if (orderLegs.length < 2) return false;
        for (const l of orderLegs) {
          if (!l.expirationDate || !l.strike || parseFloat(l.strike) <= 0) return false;
        }
      } else {
        if (!orderExpiration || !orderStrike || parseFloat(orderStrike) <= 0) return false;
      }
    }
    return true;
  };

  const submitOrder = async () => {
    setOrderConfirmOpen(false);
    setOrderSubmitting(true);
    setOrderError(null);
    setOrderResult(null);
    try {
      const r = await apiRequest(`/api/robinhood-api/orders/place/${accountId}`, {
        method: 'POST',
        body: JSON.stringify(buildOrderPayload()),
      });
      setOrderResult(r.data);
      if (!r.data?.success) {
        setOrderError(r.data?.error || 'Order placement failed');
      }
    } catch (e) {
      const msg = e.response?.data?.error || e.message || String(e);
      setOrderError(msg);
      setOrderResult({ success: false, error: msg });
    } finally {
      setOrderSubmitting(false);
    }
  };

  const Btn = ({ onClick, k, disabled, label, sx = {} }) => (
    <Button variant="outlined" size="small" onClick={onClick} disabled={loading[k] || disabled} sx={{ mr: 1, mb: 1, ...sx }}>
      {loading[k] ? <CircularProgress size={16} /> : label}
    </Button>
  );

  return (
    <Container sx={{ py: 3 }}>
      <Alert severity="info" sx={{ mb: 2 }}>This page uses the Python robin_stocks library.</Alert>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>Library Status</Typography>
        {errors.library && <Alert severity="error" sx={{ mb: 1 }}>{errors.library}</Alert>}
        {libraryStatus && <Typography variant="body2" sx={{ mb: 1 }}>{libraryStatus.message || JSON.stringify(libraryStatus)}</Typography>}
        <Btn onClick={checkLibrary} k="library" label="Check Library" />
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>Account & Connection</Typography>
        {errors.accounts && <Alert severity="error" sx={{ mb: 1 }}>{errors.accounts}</Alert>}
        {accounts.length === 0 && !loading.accounts && <Alert severity="warning" sx={{ mb: 1 }}>No Robinhood accounts found. Add one on the Accounts page first.</Alert>}
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Account</InputLabel>
          <Select value={accountId} onChange={e => setAccountId(e.target.value)} label="Account" disabled={accounts.length === 0}>
            {accounts.map(a => <MenuItem key={a.id} value={a.id}>{a.name} ({a.id})</MenuItem>)}
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <TextField label="Username" size="small" value={username} onChange={e => setUsername(e.target.value)} sx={{ width: 160 }} />
          <TextField label="Password" type="password" size="small" value={password} onChange={e => setPassword(e.target.value)} sx={{ width: 160 }} />
          <TextField label="MFA (optional)" size="small" value={mfa} onChange={e => setMfa(e.target.value)} sx={{ width: 140 }} />
        </Box>
        <Box>
          <Btn onClick={testConnection} k="connection" disabled={!accountId} label="Test Connection" />
        </Box>
        {errors.connection && <Alert severity="error" sx={{ mt: 1 }}>{errors.connection}</Alert>}
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>Feature Tests</Typography>
        <Box sx={{ mb: 2 }}>
          <Btn onClick={fetchPositions} k="positions" disabled={!accountId} label="Positions" />
          <Btn onClick={fetchOrders} k="orders" disabled={!accountId} label="Open Orders" />
          <Btn onClick={fetchOrderHistory} k="orderHistory" disabled={!accountId} label="Order History" />
          <Btn onClick={fetchProfile} k="profile" disabled={!accountId} label="Profile" />
          <Btn onClick={fetchDividends} k="dividends" disabled={!accountId} label="Dividends" />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <TextField label="Symbols" size="small" value={symbols} onChange={e => setSymbols(e.target.value)} sx={{ width: 200 }} />
          <Btn onClick={fetchQuotes} k="quotes" disabled={!accountId} label="Quotes" />
        </Box>
        {['positions','orders','orderHistory','profile','dividends','quotes']
          .filter(k => errors[k])
          .map(k => <Alert key={k} severity="error" sx={{ mb: 1 }}>{errors[k]}</Alert>)}
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>Order Entry</Typography>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This places a <strong>real, live order</strong> on the selected Robinhood account. There is no paper/simulation mode here.
        </Alert>

        <ToggleButtonGroup
          value={orderAssetType}
          exclusive
          size="small"
          onChange={(e, val) => { if (val) setOrderAssetType(val); }}
          sx={{ mb: 2 }}
        >
          <ToggleButton value="EQUITY">Equity</ToggleButton>
          <ToggleButton value="OPTION">Option</ToggleButton>
        </ToggleButtonGroup>

        {orderAssetType === 'OPTION' && (
          <Box sx={{ mb: 2 }}>
            <ToggleButtonGroup
              value={orderSpreadMode}
              exclusive
              size="small"
              onChange={(e, val) => { if (val) setOrderSpreadMode(val); }}
              sx={{ mr: 2 }}
            >
              <ToggleButton value="single">Single Leg</ToggleButton>
              <ToggleButton value="multi">Multi-Leg (Spread)</ToggleButton>
            </ToggleButtonGroup>
            {orderSpreadMode === 'multi' && (
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Direction</InputLabel>
                <Select value={orderDirection} label="Direction" onChange={e => setOrderDirection(e.target.value)}>
                  <MenuItem value="debit">Debit</MenuItem>
                  <MenuItem value="credit">Credit</MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
        )}

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={orderAssetType === 'OPTION' ? 3 : 4}>
            <TextField
              fullWidth size="small" label="Symbol"
              value={orderSymbol}
              onChange={e => setOrderSymbol(e.target.value.toUpperCase())}
              placeholder="e.g. AAPL"
            />
          </Grid>
          {orderAssetType === 'OPTION' && orderSpreadMode === 'single' && (
            <>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth size="small" type="date" label="Expiration"
                  value={orderExpiration}
                  onChange={e => setOrderExpiration(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField
                  fullWidth size="small" label="Strike"
                  value={orderStrike}
                  onChange={e => setOrderStrike(e.target.value)}
                  placeholder="e.g. 150"
                />
              </Grid>
              <Grid item xs={6} sm={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select value={orderPutCall} label="Type" onChange={e => setOrderPutCall(e.target.value)}>
                    <MenuItem value="call">Call</MenuItem>
                    <MenuItem value="put">Put</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Effect</InputLabel>
                  <Select value={orderPositionEffect} label="Effect" onChange={e => setOrderPositionEffect(e.target.value)}>
                    <MenuItem value="open">Open</MenuItem>
                    <MenuItem value="close">Close</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}
          {orderAssetType === 'OPTION' && orderSpreadMode === 'single' && (
            <Grid item xs={6} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Side</InputLabel>
                <Select value={orderSide} label="Side" onChange={e => setOrderSide(e.target.value)}>
                  <MenuItem value="buy">Buy</MenuItem>
                  <MenuItem value="sell">Sell</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
          {orderAssetType !== 'OPTION' && (
            <Grid item xs={6} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Side</InputLabel>
                <Select value={orderSide} label="Side" onChange={e => setOrderSide(e.target.value)}>
                  <MenuItem value="buy">Buy</MenuItem>
                  <MenuItem value="sell">Sell</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={6} sm={2}>
            <TextField
              fullWidth size="small" type="number" label="Quantity"
              value={orderQuantity}
              onChange={e => setOrderQuantity(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small" disabled={orderAssetType === 'OPTION'}>
              <InputLabel>Order Type</InputLabel>
              <Select value={orderType} label="Order Type" onChange={e => setOrderType(e.target.value)}>
                <MenuItem value="MARKET">Market</MenuItem>
                <MenuItem value="LIMIT">Limit</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              fullWidth size="small" label="Limit Price"
              value={orderPrice}
              onChange={e => setOrderPrice(e.target.value)}
              disabled={orderType !== 'LIMIT'}
              placeholder={orderSpreadMode === 'multi' ? 'Net price' : 'e.g. 1.50'}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Time in Force</InputLabel>
              <Select value={orderTimeInForce} label="Time in Force" onChange={e => setOrderTimeInForce(e.target.value)}>
                <MenuItem value="gtc">GTC</MenuItem>
                <MenuItem value="gfd">Day (GFD)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {orderAssetType === 'OPTION' && orderSpreadMode === 'multi' && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">Legs ({orderLegs.length})</Typography>
              <Tooltip title="Add leg">
                <IconButton size="small" onClick={addLeg} sx={{ ml: 1 }}><AddIcon /></IconButton>
              </Tooltip>
            </Box>
            {orderLegs.map((leg, idx) => (
              <Box key={idx} sx={{ mb: 1 }}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} sm={2}>
                    <TextField
                      fullWidth size="small" type="date" label={`Exp (Leg ${idx + 1})`}
                      value={leg.expirationDate}
                      onChange={e => updateLeg(idx, 'expirationDate', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField
                      fullWidth size="small" label="Strike"
                      value={leg.strike}
                      onChange={e => updateLeg(idx, 'strike', e.target.value)}
                      placeholder="e.g. 150"
                    />
                  </Grid>
                  <Grid item xs={6} sm={1}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Type</InputLabel>
                      <Select value={leg.optionType} label="Type" onChange={e => updateLeg(idx, 'optionType', e.target.value)}>
                        <MenuItem value="call">Call</MenuItem>
                        <MenuItem value="put">Put</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Action</InputLabel>
                      <Select value={leg.action} label="Action" onChange={e => updateLeg(idx, 'action', e.target.value)}>
                        <MenuItem value="buy">Buy</MenuItem>
                        <MenuItem value="sell">Sell</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Effect</InputLabel>
                      <Select value={leg.effect} label="Effect" onChange={e => updateLeg(idx, 'effect', e.target.value)}>
                        <MenuItem value="open">Open</MenuItem>
                        <MenuItem value="close">Close</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={4} sm={1}>
                    <TextField
                      fullWidth size="small" type="number" label="Ratio"
                      value={leg.ratioQuantity}
                      onChange={e => updateLeg(idx, 'ratioQuantity', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={2} sm={2}>
                    {orderLegs.length > 2 && (
                      <IconButton size="small" color="error" onClick={() => removeLeg(idx)}>
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Grid>
                </Grid>
                {idx < orderLegs.length - 1 && <Divider sx={{ my: 0.5 }} />}
              </Box>
            ))}
          </Box>
        )}

        <Button
          variant="contained"
          color={orderSide === 'buy' ? 'primary' : 'secondary'}
          disabled={!isOrderValid() || orderSubmitting}
          onClick={() => setOrderConfirmOpen(true)}
        >
          {orderSubmitting ? <CircularProgress size={20} /> : `${orderAssetType === 'OPTION' && orderSpreadMode === 'multi' ? `Place ${orderDirection} Spread` : `${orderSide === 'buy' ? 'Buy' : 'Sell'} ${orderAssetType === 'OPTION' ? 'Option' : 'Stock'}`}`}
        </Button>

        {orderError && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setOrderError(null)}>{orderError}</Alert>}
        {orderResult?.success && (
          <Alert severity="success" sx={{ mt: 2 }} onClose={() => setOrderResult(null)}>
            Order placed successfully.
          </Alert>
        )}
        {orderResult && (
          <Box component="pre" sx={{ mt: 2, bgcolor: 'grey.900', color: 'grey.100', p: 1, overflow: 'auto', maxHeight: 300, fontSize: '0.75rem' }}>
            {JSON.stringify(orderResult, null, 2)}
          </Box>
        )}
      </Paper>

      <Dialog open={orderConfirmOpen} onClose={() => setOrderConfirmOpen(false)}>
        <DialogTitle>Confirm Live Order</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>This will place a real order with real money.</Alert>
          <Box component="pre" sx={{ bgcolor: 'grey.100', p: 1, fontSize: '0.8rem' }}>
            {JSON.stringify(buildOrderPayload(), null, 2)}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrderConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color={orderSide === 'buy' ? 'primary' : 'secondary'} onClick={submitOrder}>
            Confirm & Place Order
          </Button>
        </DialogActions>
      </Dialog>

      {result && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Result</Typography>
          {result.error && <Alert severity="error" sx={{ mb: 1 }}>{result.error}</Alert>}
          {result.data && (
            <Box component="pre" sx={{ bgcolor: 'grey.900', color: 'grey.100', p: 1, overflow: 'auto', maxHeight: 400, fontSize: '0.75rem' }}>
              {JSON.stringify(result.data, null, 2)}
            </Box>
          )}
        </Paper>
      )}
    </Container>
  );
}
